import { enqueueSnackbar } from "notistack";
import { useApiClient } from "../../../api";
import { useExportJobs } from "../../../hooks/useExportJobs";

// Hook for exporting a single assignment as PDF
const useExportAssignment = () => {
  const ApiClient = useApiClient();
  const { addJob } = useExportJobs();

  const exportAssignment = async (assignment) => {
    try {
      const response = await ApiClient.assignment.createAssignmentAsPDF({
        assignment: assignment.id,
      });

      if (response.success) {
        addJob(response.job, {
          filename: `Assignment ${assignment.name}.pdf`,
          message: "Export requested, your download will start when ready.",
        });
      } else {
        enqueueSnackbar("Error Generating PDF - please try again!", {
          variant: "error",
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      console.error("Failed to create assignment PDF", error);
      enqueueSnackbar("Unexpected error generating PDF", {
        variant: "error",
        autoHideDuration: 3000,
      });
    }
  };

  return { exportAssignment };
};

// Hook for exporting all feedback for an assignment
const useExportAssignmentFeedback = () => {
  const ApiClient = useApiClient();
  const { addJob } = useExportJobs();

  const exportAssignmentFeedback = async (assignment) => {
    try {
      const response = await ApiClient.assignment.createAllFeedbackAsPDF({
        assignment: assignment.id,
      });

      if (response.success) {
        addJob(response.job, {
          filename: `Assignment ${assignment.name} feedback.pdf`,
        });
      } else {
        enqueueSnackbar("Error Generating Feedback PDF - please try again!", {
          variant: "error",
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      console.error("Failed to create feedback PDF", error);
      enqueueSnackbar("Unexpected error generating feedback PDF", {
        variant: "error",
        autoHideDuration: 3000,
      });
    }
  };

  return { exportAssignmentFeedback };
};
// Hook for exporting student feedback for an assignment

const useExportStudentFeedback = () => {
  const ApiClient = useApiClient();
  const { addJob } = useExportJobs();

  const exportStudentFeedback = async (assignment, student) => {
    try {
      const response = await ApiClient.assignment.createSingleFeedbackAsPDF({
        assignment: assignment.id,
        student: student.id,
      });
      if (response.success) {
        addJob(response.job, {
          filename: `${student.identifier} feedback.pdf`,
        });
      } else {
        enqueueSnackbar("Error Generating feedback PDF - please try again!", {
          variant: "error",
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      console.error("Failed to create feedback doc", error);
    }
  };

  return { exportStudentFeedback };
};

const useExportCoverSheets = () => {
  const ApiClient = useApiClient();
  const { addJob } = useExportJobs();

  const exportCoverSheets = async (assignment) => {
    try {
      const response = await ApiClient.assignment.createCoverSheetsAsPDF({
        assignment: assignment.id,
      });

      if (response.success) {
        addJob(response.job, {
          filename: `${assignment.name} Cover Sheets.pdf`,
          message:
            "Cover sheets requested, your download will start when ready.",
        });
      } else {
        enqueueSnackbar("Error generating cover sheets - please try again!", {
          variant: "error",
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      console.error("Failed to create cover sheets PDF", error);
      enqueueSnackbar("Unexpected error generating cover sheets", {
        variant: "error",
        autoHideDuration: 3000,
      });
    }
  };

  return { exportCoverSheets };
};

const useExportResults = () => {
  const ApiClient = useApiClient();
  const { addJob } = useExportJobs();

  const exportResults = async (assignment) => {
    try {
      const response = await ApiClient.assignment.exportAssignmentResultsToCsv({
        assignment: assignment.id,
      });

      if (response.success) {
        addJob(response.job, {
          filename: `${assignment.name} results.csv`,
          message: "Results will be downloaded when ready",
        });
      } else {
        enqueueSnackbar("Error generating results - please try again!", {
          variant: "error",
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      console.error("Failed to generater results", error);
      enqueueSnackbar("Unexpected error generating results", {
        variant: "error",
        autoHideDuration: 3000,
      });
    }
  };

  return { exportResults };
};

const useExportAssignmentGroupResults = () => {
  const ApiClient = useApiClient();
  const { addJob } = useExportJobs();

  const exportAssignmentGroupResults = async (assignmentGroup, schools) => {
    try {
      const response =
        await ApiClient.assignment.exportAssignmentGroupResultsToCsv({
          assignmentGroup: assignmentGroup.id,
          schools,
        });

      if (response.success) {
        addJob(response.job, {
          filename: `${assignmentGroup.name} results.csv`,
          message: "Results will be downloaded when ready",
        });
      } else {
        enqueueSnackbar(
          response.message || "Error generating results - please try again!",
          {
            variant: "error",
            autoHideDuration: 3000,
          },
        );
      }
    } catch (error) {
      console.error("Failed to generate results", error);
      enqueueSnackbar("Unexpected error generating results", {
        variant: "error",
        autoHideDuration: 3000,
      });
    }
  };

  return { exportAssignmentGroupResults };
};

const useExportAssignmentGroupCoverSheets = () => {
  const ApiClient = useApiClient();
  const { addJob } = useExportJobs();

  const exportAssignmentGroupCoverSheets = async (assignmentGroup) => {
    try {
      const response =
        await ApiClient.assignment.createAssignmentGroupCoverSheets({
          assignmentGroup: assignmentGroup.id,
        });

      if (response.success) {
        addJob(response.job, {
          filename: `${assignmentGroup.name} Cover Sheets.zip`,
          message:
            "Cover sheets requested, your download will start when ready.",
        });
      } else {
        enqueueSnackbar("Error generating cover sheets - please try again!", {
          variant: "error",
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      console.error(
        "Failed to create assignment group cover sheets PDF",
        error,
      );
      enqueueSnackbar("Unexpected error generating cover sheets", {
        variant: "error",
        autoHideDuration: 3000,
      });
    }
  };

  return { exportAssignmentGroupCoverSheets };
};

export {
  useExportAssignment,
  useExportAssignmentFeedback,
  useExportStudentFeedback,
  useExportCoverSheets,
  useExportResults,
  useExportAssignmentGroupResults,
  useExportAssignmentGroupCoverSheets,
};
